import { addDays, todayCalendarDate } from '../../../../components/calendar/monthGrid';

/**
 * Sample values for the Raise inventory audit form.
 *
 * <b>It fills every REQUIRED field, which is the Autogenerate contract: the primary action
 * enables on the first try.</b> The stores are the one required picker, so a generator that left
 * them empty would produce a form that cannot be submitted — which is the failure the contract
 * exists to prevent.
 *
 * <b>Foreign keys are resolved against what the form already loaded</b>, never invented: the
 * store ids and the auditor id are picked from the live options, so the server's "it must exist
 * and be active" check passes rather than reporting what reads as a bug in the form.
 *
 * <b>NOTHING here reaches the identity store.</b> An audit names an employee by id and stores no
 * name at all, so there is no fabricated person to worry about — which is why the notes are
 * plausible vocabulary rather than the obviously-synthetic values `samplePersonnel` produces.
 * The rule is about where a value LANDS, and none of this lands in `rsys_pii`.
 */
const NOTES = [
  'Quarterly physical stock take across the main store.',
  'Month-end inventory reconciliation.',
  'Spot count following a discrepancy report.',
  'Year-end wall-to-wall count.',
  'Cycle count over the fast-moving supply items.',
];

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

export interface SampleInventoryAudit {
  auditStartDate: string;
  auditEndDate: string;
  notes: string;
  storageFacilityIds: string[];
  auditedByPersonnelId: string | null;
}

export function sampleInventoryAudit(
  facilityIds: readonly string[],
  personnelIds: readonly string[],
): SampleInventoryAudit {
  const start = todayCalendarDate();

  return {
    auditStartDate: start,

    // A window of today to three days out — a stock take takes a walk, not an instant.
    auditEndDate: addDays(start, pick([0, 1, 2, 3])),

    notes: pick(NOTES),

    /*
      EVERY store the form offers, rather than one at random. A generated audit is most useful
      when it exercises the per-store breakdown behind each count, and a single-store sheet never
      draws it — which is the half of this form a reader most needs to see working.
    */
    storageFacilityIds: [...facilityIds],

    // Null where the picker is empty or withheld, which is a real state: the field is optional.
    auditedByPersonnelId: personnelIds.length === 0 ? null : pick(personnelIds),
  };
}
